// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
/**
 * Dado de leitura do Painel Executivo, resolvido a partir de
 * `dados-simulados.json`.
 *
 * NENHUMA DATA LITERAL. O JSON guarda a distância em meses até hoje
 * (`mesesAtras`, `mesReferenciaAtras`) e, nas duas datas de sincronização, o
 * dia do mês e a hora. Este arquivo monta `AAAA-MM-01` e o ISO com hora. As
 * séries mantêm a ordem e o tamanho do JSON (12 e 6 meses). Ano-mês escrito à
 * mão envelhece sozinho (CLAUDE.md, "Nenhuma competência literal no mock");
 * `dadosSimulados.spec.ts` falha se alguém escrever um no JSON.
 */

import { instanteAtras, primeiroDiaDoMesAtras, type InstanteRelativo } from '../datasRelativas';
import DADOS_SIMULADOS from './dados-simulados.json';
import type {
  CadinResumo,
  DadosPainelExecutivo,
  MixModalidade,
  OperacaoImediata,
  PontoEvolucaoCompetencia,
  PontoRevisoesAjustes,
  ReservaSof,
  ResumoMes,
} from './_secoes/tipos';

type ComMesesAtras<T extends { mes: string }> = Omit<T, 'mes'> & { mesesAtras: number };
type ComMesReferenciaAtras<T extends { mesReferencia: string }> = Omit<T, 'mesReferencia'> & {
  mesReferenciaAtras: number;
};

interface DadosBrutos extends Omit<
  DadosPainelExecutivo,
  | 'resumoMes'
  | 'reservaSof'
  | 'operacaoImediata'
  | 'mixModalidade'
  | 'revisoesAjustesSerie'
  | 'evolucaoPorCompetencia'
> {
  resumoMes: ComMesReferenciaAtras<ResumoMes>;
  reservaSof: Omit<ReservaSof, 'ultimaImportacao'> & {
    ultimaImportacao: InstanteRelativo | null;
  };
  operacaoImediata: Omit<OperacaoImediata, 'cadin'> & {
    cadin: Omit<CadinResumo, 'ultimaConsulta'> & { ultimaConsulta: InstanteRelativo | null };
  };
  mixModalidade: ComMesReferenciaAtras<MixModalidade>;
  revisoesAjustesSerie: ComMesesAtras<PontoRevisoesAjustes>[];
  evolucaoPorCompetencia: ComMesesAtras<PontoEvolucaoCompetencia>[];
}

function porMes<T extends { mes: string }>(pontos: ComMesesAtras<T>[], hoje: Date): T[] {
  return pontos.map(({ mesesAtras, ...ponto }) => ({
    ...ponto,
    mes: primeiroDiaDoMesAtras(mesesAtras, hoje),
  })) as unknown as T[];
}

function instanteOuNulo(instante: InstanteRelativo | null, hoje: Date): string | null {
  return instante ? instanteAtras(instante, hoje) : null;
}

export function carregarPainelExecutivo(hoje = new Date()): DadosPainelExecutivo {
  const bruto = DADOS_SIMULADOS as unknown as DadosBrutos;
  const { mesReferenciaAtras: mesDoResumo, ...resumoMes } = bruto.resumoMes;
  const { mesReferenciaAtras: mesDoMix, ...mixModalidade } = bruto.mixModalidade;
  const { cadin } = bruto.operacaoImediata;

  return {
    ...bruto,
    resumoMes: { ...resumoMes, mesReferencia: primeiroDiaDoMesAtras(mesDoResumo, hoje) },
    reservaSof: {
      ...bruto.reservaSof,
      ultimaImportacao: instanteOuNulo(bruto.reservaSof.ultimaImportacao, hoje),
    },
    operacaoImediata: {
      ...bruto.operacaoImediata,
      cadin: { ...cadin, ultimaConsulta: instanteOuNulo(cadin.ultimaConsulta, hoje) },
    },
    mixModalidade: { ...mixModalidade, mesReferencia: primeiroDiaDoMesAtras(mesDoMix, hoje) },
    revisoesAjustesSerie: porMes<PontoRevisoesAjustes>(bruto.revisoesAjustesSerie, hoje),
    evolucaoPorCompetencia: porMes<PontoEvolucaoCompetencia>(bruto.evolucaoPorCompetencia, hoje),
  };
}

export const DADOS_PAINEL_EXECUTIVO = carregarPainelExecutivo();
